import { useState, useRef, RefObject, useCallback } from 'react'
import { IGridItem, IPositionParams, IPoint, IActiveElement } from '../../../grid.types'
import useMouseDragHandler from './useMouseDragHandler'
import calculatePositionUtils from '../calculatePosition.utils'

const useDragHandlers = (
  containerRef: RefObject<HTMLDivElement>,
  handleRef: RefObject<HTMLDivElement>,
  gridItem: IGridItem,
  posParams: IPositionParams,
  onActiveElementChange: (activeElement: IActiveElement) => void,
  onDragMove: (x: number, y: number, key: IGridItem['key']) => void
) => {
  const [draggingPos, setDraggingPos] = useState<IPoint | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const prevItemPos = useRef({
    x: gridItem.x * calculatePositionUtils.calcGridColWidth(posParams),
    y: gridItem.y * posParams.rowHeight,
  })

  const onStart = useCallback(() => {
    if (!containerRef.current) return
    setIsDragging(true)
    onActiveElementChange({ key: gridItem.key, movedType: 'dragging' })
    containerRef.current.style.transition = 'none'
    containerRef.current.style.userSelect = 'none'
    containerRef.current.style.zIndex = '3'
    handleRef.current.style.cursor = 'grabbing'
    document.body.style.cursor = 'grabbing'
  }, [containerRef, gridItem, onActiveElementChange])

  const onMove = useCallback(
    (delta: IPoint) => {
      const dx = prevItemPos.current.x + delta.x
      const dy = prevItemPos.current.y + delta.y
      const { x, y } = calculatePositionUtils.calcXY(posParams, dy, dx, gridItem.width, gridItem.height)
      onDragMove(x, y, gridItem.key)
      setDraggingPos({ x: dx, y: dy })
    },
    [posParams, gridItem, onDragMove]
  )

  const onEnd = useCallback(() => {
    if (!containerRef.current) return
    setIsDragging(false)
    onActiveElementChange(null)
    containerRef.current.style.transition = 'all 0.2s ease'
    containerRef.current.style.zIndex = '2'
    containerRef.current.style.userSelect = 'text'
    document.body.style.cursor = ''
    handleRef.current.style.cursor = 'grab'
    setDraggingPos(null)
  }, [containerRef, onActiveElementChange])

  useMouseDragHandler({
    ref: handleRef,
    enabled: posParams.containerWidth != null,
    onStart,
    onMove,
    onEnd,
  })

  return { isDragging, draggingPos, prevItemPos }
}

export default useDragHandlers
